
import { UserSummary } from "@/api/dtos/Manager.schema";
import { Component, Vue } from "vue-property-decorator";
import { DataTableItemProps } from "vuetify";
import EditFrame from "../EditFrame.vue";
import moment from "moment";
import { User } from "@/api/dtos/User.schema";
import DangerButton from "../buttons/DangerButton.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import AddPersonDialog from "@/components/dialogs/AddPersonDialog.vue";
import DangerDialog from "../dialogs/DangerDialog.vue";
import api from "@/api";
import { UsersModule } from "@/store/modules/UsersModule";
import { OperatorsModule } from "@/store/modules/OperatorsModule";
import { OperatorSummary } from "@/api/dtos/Operator.schema";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

@Component({
  components: {
    EditFrame,
    DangerButton,
    AddButton,
    AddPersonDialog,
    DangerDialog,
  },
})
export default class UserManagement extends Vue {
  moment = moment;

  openTab: string | null = null;
  dialog = false;
  selectedUser: User | null = null;
  selectedUserIsEnduser = false; // TODO add User.isEnduser and remove this variable
  queryUserType:
    | "Alle"
    | "Endnutzer"
    | "Stations-Manager"
    | "Eigentümer"
    | "Admin"
    | "Keine" = "Alle";

  queryUserString = "";
  showBanUserDialog = false;
  showDeleteAccountDialog = false;
  showDeleteEnduserDialog = false;
  showUnbanUserDialog = false;
  showDeleteAdminDialog = false;

  readonly headers: {
    text: string;
    value: keyof UserSummary;
    align?: "start" | "center" | "end";
  }[] = [
    { text: "Email", value: "email" },
    { text: "Letzter Login", value: "lastLogin" },
    { text: "Gesperrt", value: "banned" },
  ];

  get filteredUsers(): UserSummary[] {
    return this.users.filter((user) => {
      if (!user.email.includes(this.queryUserString)) return false;
      switch (this.queryUserType) {
        case "Alle":
          return true;
        case "Endnutzer":
          if (user.enduser) return true;
          break;
        case "Stations-Manager":
          if (user.manager) return true;
          break;
        case "Eigentümer":
          if (user.owner) return true;
          break;
        case "Admin":
          return user.admin;
        case "Keine":
          return user.banned;
        default:
          return false;
      }
    });
  }

  mounted(): void {
    UsersModule.fetchUserSummaries();
    OperatorsModule.fetchOperatorSummaries();
  }

  get users(): UserSummary[] {
    return UsersModule.users;
  }

  get operatorSummaries(): OperatorSummary[] {
    return OperatorsModule.operatorSummaries;
  }

  get selectedUsersOperatorsLinks(): {
    operatorName: string;
    operatorUuid: string;
  }[] {
    if (!this.selectedUser || this.selectedUser.ownerOf.length <= 0) return [];
    if (this.operatorSummaries.length <= 0) return [];

    return this.selectedUser.ownerOf.map((operatorUuid) => {
      const operatorSummary: OperatorSummary = this.operatorSummaries.find(
        (operatorSummary) => operatorSummary.uuid === operatorUuid
      )!;
      return {
        operatorName: operatorSummary.name,
        operatorUuid: operatorSummary.uuid,
      };
    });
  }

  async onUserClicked(
    userSummary: UserSummary,
    row: DataTableItemProps
  ): Promise<void> {
    row.select(true);
    this.selectedUser = await api.UsersApi.getUser(userSummary.userUuid);
    this.selectedUserIsEnduser = userSummary.enduser;

    this.$nextTick(() => {
      const usersCard = this.$el.querySelector(".users-card");
      if (usersCard && this.selectedUser) {
        usersCard.classList.add("has-detail");
      }
    });
  }

  async onAddAdmin(email: string): Promise<void> {
    this.dialog = false;
    try {
      const res = await api.AdminApi.addAdmin(email);
      // TODO: Show notification about sent email to user and wait for registration

      showToast({
        message: `Einladung an ${email} wurde versendet`,
        type: "success",
      });
    } catch (e: any) {
      console.error("Error adding admin:", e);
      showToast({
        response: getAxiosResponse(e),
        message: `Einladung konnte nicht versendet werden`,
        type: "error",
      });
    }
  }

  async refreshSelectedUser(): Promise<void> {
    if (!this.selectedUser) return;
    this.selectedUser = await api.UsersApi.getUser(this.selectedUser.uuid);
  }

  async onAccountDelete(): Promise<void> {
    try {
      await api.UsersApi.deleteUser(this.selectedUser!.uuid);
      this.selectedUser = null;
      showToast({
        message: "Benutzerkonto wurde gelöscht",
        type: "success",
      });
    } catch (e) {
      console.error("Error deleting user:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Benutzerkonto konnte nicht gelöscht werden",
        type: "error",
      });
    }
  }

  async onBanUser(): Promise<void> {
    try {
      await api.UsersApi.updateIsUserBanned(this.selectedUser!.uuid, true);
      this.refreshSelectedUser();
      showToast({
        message: "Benutzer wurde gesperrt",
        type: "success",
      });
    } catch (e) {
      console.error("Error banning user:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Benutzer konnte nicht gesperrt werden",
        type: "error",
      });
    }
  }

  async onUnbanUser(): Promise<void> {
    try {
      await api.UsersApi.updateIsUserBanned(this.selectedUser!.uuid, false);
      this.refreshSelectedUser();
      showToast({
        message: "Benutzer wurde entsperrt",
        type: "success",
      });
    } catch (e) {
      console.error("Error unbanning user:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Benutzer konnte nicht entsperrt werden",
        type: "error",
      });
    }
  }

  async onEnduserDataDelete(): Promise<void> {
    await api.UsersApi.updateUserAddress(this.selectedUser!.uuid, null);
    this.refreshSelectedUser();
  }

  async onAdminDataDelete(): Promise<void> {
    await api.AdminApi.deleteAdmin(this.selectedUser!.uuid);
    this.selectedUser = null;
  }
}
